import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SignOut, House, Gear, DownloadSimple, Key, Bell, Flask, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { canSeeSettings, canUseApp } from '@/auth/AuthContext';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useT } from '@/i18n/LanguageContext';
import { ROLE_DOT, userInitial, userRoleKey } from './headerConstants';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** Right-aligned avatar pill + dropdown with 通知 / ホーム / 設定 / ログアウト. */
export default function AccountMenu({ user, open, onOpenChange, onLogout, plannerAlerts = { total: 0, my_open: [], my_open_count: 0, my_blocked_count: 0 } }) {
  const navigate = useNavigate();
  const { t } = useT();
  const { canInstall, installed, isIOS, promptInstall } = useInstallPrompt();
  const canTestMode = user && (user.role === 'admin' || user.role === 'manager');
  const [tm, setTm] = useState(null);
  const [tmBusy, setTmBusy] = useState(false);
  // Launcher/PartsRequest render this menu UNCONTROLLED (no `open` prop), so
  // track open state locally and mirror it up when a handler is provided.
  const [menuOpen, setMenuOpen] = useState(false);
  const handleOpenChange = (v) => { setMenuOpen(v); onOpenChange?.(v); };
  useEffect(() => {
    if (!menuOpen || !canTestMode) return;
    axios.get(`${API}/api/test-mode`).then((r) => setTm(r.data)).catch(() => {});
  }, [menuOpen, canTestMode]);
  if (!user || typeof user !== 'object') return null;
  const initial = userInitial(user);
  const roleKey = userRoleKey(user);
  const showSettings = canSeeSettings(user) && canUseApp(user, 'settings');
  const showInstall = !installed && (canInstall || isIOS);
  const showPlanner = canUseApp(user, 'planner');
  // B8 — バッジ・サイドバーの要対応・ホームのカードは同じ数字を読む:
  // 自分に割り当てられた未完了タスク（ブロック中も含む・内訳は別表示）。
  const alertItems = plannerAlerts.my_open || [];
  const alertTotal = plannerAlerts.my_open_count || alertItems.length;
  const blockedCount = plannerAlerts.my_blocked_count || 0;
  const go = (path) => { onOpenChange?.(false); navigate(path); };

  const handleInstall = async () => {
    if (canInstall) {
      const ok = await promptInstall();
      if (ok) toast.success(t('account.installedAddedToast'));
    } else if (isIOS) {
      toast.info(t('account.installIosHint'));
    }
  };

  // Verbose error surfacing — the previous fallback ("テストモードの開始に失敗しました")
  // silently swallowed the actual status/network reason on production, making
  // reverse-proxy timeouts indistinguishable from a real server error.
  const formatTmError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (detail) return `${fallback}：${detail}`;
    const status = err?.response?.status;
    if (status) return `${fallback}（HTTP ${status}）`;
    // No response = network error / CORS / proxy timeout. `err.code` is
    // 'ECONNABORTED' on axios timeout, otherwise err.message is 'Network Error'.
    const reason = err?.code || err?.message || 'ネットワークエラー';
    return `${fallback}（${reason}）— 通信が中断された可能性があります。しばらく待ってから再度お試しください`;
  };

  const toggleTestMode = async () => {
    if (tmBusy) return;
    if (tm?.active) {
      if (!window.confirm('テストモードを終了しますか？\n\n⚠️ あなたのサンドボックスは削除され、テストモード中の変更はすべて破棄されます。本番データには影響ありません。')) return;
      setTmBusy(true);
      try {
        // 3-minute ceiling: production drop-sandbox on real data can take ~10-30s;
        // 180s gives a large safety margin without leaving the button spinning forever.
        await axios.post(`${API}/api/test-mode/disable`, { confirm: true }, { timeout: 180000 });
        toast.success('テストモードを終了しました（サンドボックスを削除）');
        window.dispatchEvent(new Event('tm-changed'));
        setTm((p) => ({ ...p, active: false }));
      } catch (err) { toast.error(formatTmError(err, 'テストモードの終了に失敗しました')); }
      finally { setTmBusy(false); }
    } else {
      if (!window.confirm('テストモードを開始しますか？（あなた専用）\n\n・現在のデータをあなた専用のサンドボックスにコピーします\n・あなたの操作は本番データや他のユーザーに一切影響しません\n・データ量に応じて 数十秒〜1分ほどかかる場合があります\n・終了時に変更はすべて破棄されます')) return;
      setTmBusy(true);
      try {
        // Long timeout — copying 100+ collections of real production data
        // can legitimately take 30-120s. Frontend axios default has no timeout
        // but we set an explicit 3-minute ceiling as a safety net.
        await axios.post(`${API}/api/test-mode/enable`, {}, { timeout: 180000 });
        toast.success('テストモードを開始しました（あなたのみ有効）');
        window.dispatchEvent(new Event('tm-changed'));
        setTm((p) => ({ ...p, active: true }));
      } catch (err) { toast.error(formatTmError(err, 'テストモードの開始に失敗しました')); }
      finally { setTmBusy(false); }
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-account"
          className="inline-flex items-center gap-2 px-2 sm:px-2.5 py-1.5 rounded-full text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
        >
          <span className="relative inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-zinc-100 text-zinc-700 text-xs font-bold shadow-sm">
            {initial}
            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${ROLE_DOT[roleKey]}`} />
            {showPlanner && alertTotal > 0 && (
              <span data-testid="account-alert-badge" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                {alertTotal > 99 ? '99+' : alertTotal}
              </span>
            )}
          </span>
          <span className="hidden lg:inline truncate max-w-[140px] text-[13px]">{user.name || user.email}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 p-1.5">
        <div className="px-2.5 py-2 mb-1 rounded-md bg-zinc-50">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 text-sm font-bold shadow-sm shrink-0">
              {initial}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-zinc-900 truncate leading-tight">{user.name}</p>
              <p className="text-[11px] text-zinc-500 truncate leading-tight">{user.email}</p>
            </div>
          </div>
          <div className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white border border-zinc-200 text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">
            <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[roleKey]}`} />
            {t(`role.${roleKey}`)}
          </div>
        </div>

        {showPlanner && (
          <>
            <div className="px-1 pt-1 pb-1.5" data-testid="account-alerts">
              <div className="flex items-center justify-between px-1.5 mb-1">
                <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider inline-flex items-center gap-1">
                  <Bell size={12} weight="fill" className={alertTotal > 0 ? 'text-rose-500' : 'text-zinc-400'} />
                  {t('planner.alerts.bell')}
                </span>
                {alertTotal > 0 && (
                  <span className="text-[10px] font-bold text-white bg-rose-500 rounded-full px-1.5 py-0.5">{t('planner.alerts.pill', { n: alertTotal })}</span>
                )}
              </div>
              {blockedCount > 0 && (
                <p className="text-[11px] font-semibold text-rose-600 px-1.5 pb-1" data-testid="account-alert-blocked">
                  {t('planner.alerts.blockedNote', { n: blockedCount })}
                </p>
              )}
              {alertItems.length === 0 ? (
                <p className="text-[11px] text-zinc-400 px-1.5 py-1.5">{t('planner.alerts.none')}</p>
              ) : (
                <div className="max-h-56 overflow-y-auto space-y-0.5">
                  {alertItems.slice(0, 3).map((tk) => (
                    <button
                      key={tk.id}
                      onClick={() => go(`/planner/project/${tk.project_id}?task=${tk.id}`)}
                      data-testid={`account-alert-${tk.id}`}
                      className="w-full text-left rounded-md px-1.5 py-1.5 hover:bg-zinc-50 flex items-center gap-2 transition-colors"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tk.overdue ? 'bg-rose-500' : 'bg-amber-500'}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] text-zinc-800 truncate leading-tight">{tk.title}</span>
                        {tk.project_name && <span className="block text-[10px] text-zinc-400 truncate leading-tight">{tk.project_name}</span>}
                      </span>
                      <span className={`text-[10px] tabular-nums shrink-0 ${tk.overdue ? 'text-rose-600 font-semibold' : 'text-zinc-400'}`}>{fmtDue(tk.due_date)}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => go('/planner/my-tasks')}
                data-testid="account-alert-all"
                className="w-full text-left text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50 rounded-md px-1.5 py-1.5 mt-0.5 transition-colors"
              >
                {alertItems.length > 3 ? t('planner.alerts.viewMore', { n: alertItems.length - 3 }) : t('planner.alerts.viewAll')} →
              </button>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => navigate('/')} data-testid="account-home" className="cursor-pointer rounded-md">
          <House size={16} weight="duotone" className="mr-2 text-teal-700" />
          {t('common.home')}
        </DropdownMenuItem>
        {showSettings && (
          <DropdownMenuItem
            onClick={() => navigate('/settings')}
            data-testid="account-settings"
            className="cursor-pointer rounded-md"
          >
            <Gear size={16} weight="duotone" className="mr-2 text-teal-700" />
            {t('common.settings')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => navigate('/change-password')}
          data-testid="account-change-password"
          className="cursor-pointer rounded-md"
        >
          <Key size={16} weight="duotone" className="mr-2 text-teal-700" />
          {t('account.changePassword')}
        </DropdownMenuItem>
        {canTestMode && (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); toggleTestMode(); }}
            data-testid="account-test-mode"
            disabled={tmBusy || !tm}
            className={`cursor-pointer rounded-md ${tm?.active ? 'text-orange-600 focus:text-orange-700 focus:bg-orange-50' : ''}`}
          >
            <Flask size={16} weight="duotone" className={`mr-2 ${tm?.active ? 'text-orange-600' : 'text-teal-700'}`} />
            {tmBusy ? '処理中...' : !tm ? 'テストモード...' : tm.active ? 'テストモードを終了' : 'テストモードを開始'}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => navigate('/demo')}
          data-testid="account-demo"
          className="cursor-pointer rounded-md"
        >
          <Sparkle size={16} weight="duotone" className="mr-2 text-fuchsia-600" />
          {t('settings.demo')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {showInstall && (
          <DropdownMenuItem
            onClick={handleInstall}
            data-testid="account-install-app"
            className="cursor-pointer rounded-md text-teal-700 focus:text-[#0040cc] focus:bg-blue-50"
          >
            <DownloadSimple size={16} weight="duotone" className="mr-2" />
            {t('common.installApp')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onLogout}
          data-testid="nav-logout"
          className="cursor-pointer rounded-md text-red-600 focus:text-red-700 focus:bg-red-50"
        >
          <SignOut size={16} weight="duotone" className="mr-2" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
